import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { debugLog, type PlacementId } from '@schibsted-nmp/advertising-shared';

import type { Api } from './Api/index.js';

const directlyRenderedSlotIds = new Set<string>();
const consumedFromDom = new Set<string>();
const observedRoots = new WeakSet<Node>();

export namespace DirectRenderedSlots {
  // Intentionally does NOT check consumedFromDom - has() must keep returning true
  // for DOM-detected slots even after consume(), because onSlotRenderEnded relies
  // on has() to prevent GAM from hiding the directly rendered ad.
  export function has(slotId: string): boolean {
    return directlyRenderedSlotIds.has(slotId) || existsInDom(slotId);
  }

  export function consume(slotId: string): boolean {
    if (directlyRenderedSlotIds.delete(slotId)) return true;
    if (consumedFromDom.has(slotId)) return false;
    if (existsInDom(slotId)) {
      consumedFromDom.add(slotId);
      return true;
    }
    return false;
  }

  function existsInDom(slotId: string): boolean {
    if (typeof document === 'undefined') return false;
    try {
      return !!document.querySelector(
        `#${CSS.escape(slotId)} [data-rlv-direct-rendered]`
      );
    } catch {
      return false;
    }
  }

  export function observe(slotId: string) {
    if (typeof document === 'undefined') return;

    const el = document.getElementById(slotId);
    const host = document.querySelector(
      `advt-component[placementid="${CSS.escape(slotId)}"]`
    );
    const root = el ?? host ?? document.body;

    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    if (el?.querySelector('[data-rlv-direct-rendered]')) {
      handleDirectRender(slotId, el);
      return;
    }

    const observer = new MutationObserver(() => {
      const target = document.getElementById(slotId);
      const hasAttr = !!target?.querySelector('[data-rlv-direct-rendered]');

      if (target && hasAttr) {
        clearTimeout(timeout);
        observer.disconnect();
        handleDirectRender(slotId, target);
      }
    });

    const timeout = setTimeout(() => {
      observer.disconnect();
    }, 30_000);

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }

  function handleDirectRender(slotId: string, el: HTMLElement) {
    el.style.removeProperty('display');
    updatePlacementKeyValueById(slotId as PlacementId, 'status', 'loaded');
    debugLog(
      `Slot '${slotId}' direct render detected by observer, status set to loaded`
    );
  }

  export function simulate(slotId: string) {
    directlyRenderedSlotIds.add(slotId);
    console.info(
      `[ADVT] Simulated direct render for slot '${slotId}' — it will be excluded from the next GAM refresh`
    );
  }

  export function register(relevantDigital: Api.Loaded) {
    relevantDigital.addAdsCallbacks({
      slotRenderEnded(ev) {
        if (!ev.directRender) return;

        const slotId = ev.slot.getSlotElementId();
        directlyRenderedSlotIds.add(slotId);

        debugLog(
          `Slot '${slotId}' was directly rendered by Relevant Yield, excluding from GAM refresh`
        );
      }
    });
  }
}
